import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import type { DataNode, EventDataNode } from 'antd/es/tree';

import { useAuthorization } from '@/shared/hooks/useAuthorization';
import { DOMAIN_HIERARCHY_LIMIT, NOTIFICATION_MESSAGES, ROUTES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth } from '@/shared/lib/format/fqn';
import { domainKeys } from '@/shared/lib/query/queryKeys';
import { sortByDisplayName } from '@/shared/lib/sort/sortByDisplayName';
import type { TreeNode } from '@/shared/lib/tree';
import { CategoryTree } from '@/shared/ui/CategoryTree';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';

import type { DomainHierarchyItem } from '@/entities/domain';
import { useDomainHierarchy } from '@/entities/domain';

import styles from './DomainCategoryTree.module.css';

const transformHierarchyToTreeNode = (domains: DomainHierarchyItem[]): TreeNode[] => {
  const sortedDomains = sortByDisplayName(domains);

  return sortedDomains.map((domain) => {
    const depth = calculateFqnDepth(domain.fullyQualifiedName);
    const hasChildren = domain.children && domain.children.length > 0;
    const isMaxDepth = depth >= TREE_DEPTH.DOMAIN;
    const isLeaf = !hasChildren || isMaxDepth;

    const children = hasChildren && !isMaxDepth ? transformHierarchyToTreeNode(domain.children) : undefined;

    return {
      key: domain.fullyQualifiedName,
      title: domain.displayName || domain.name,
      count: domain.children?.length ?? 0,
      isLeaf,
      children,
    };
  });
};

type DomainCategoryTreeProps = {
  selectedDomainFqn: string | null;
  breadcrumbPath?: string;
  handleSetBreadcrumbPath: (fqn: string) => void;
  onAddClick?: () => void;
  refreshParentFqn?: string | null;
  onRefreshComplete?: () => void;
};

export const Tree = ({
  selectedDomainFqn,
  breadcrumbPath,
  handleSetBreadcrumbPath,
  onAddClick,
  refreshParentFqn,
  onRefreshComplete,
}: DomainCategoryTreeProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasAnyRole } = useAuthorization();

  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: hierarchyData, isLoading, dataUpdatedAt } = useDomainHierarchy({ limit: DOMAIN_HIERARCHY_LIMIT });

  const treeData = useMemo(() => {
    if (!hierarchyData?.data) return [];
    return transformHierarchyToTreeNode(hierarchyData.data);
  }, [hierarchyData]);

  const prevDataUpdatedAtRef = useRef(dataUpdatedAt);

  const findKeyInTreeData = useCallback((key: string, nodes: TreeNode[]): boolean => {
    for (const node of nodes) {
      if (node.key === key) return true;
      if (node.children && findKeyInTreeData(key, node.children)) return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt !== prevDataUpdatedAtRef.current) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setIsInitialized(false);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (treeData.length === 0) return;
    if (selectedKeys.length === 0) return;

    const currentSelectedKey = selectedKeys[0] as string;
    const keyExistsInTree = findKeyInTreeData(currentSelectedKey, treeData);

    if (!keyExistsInTree) {
      const firstKey = treeData[0].key as string;
      setSelectedKeys([firstKey]);
      navigate(`${ROUTES.DOMAIN}/${encodeURIComponent(firstKey)}/sub-domains`, { replace: true });
    }
  }, [treeData, selectedKeys, findKeyInTreeData, navigate]);

  const prevRefreshParentFqnRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (refreshParentFqn === null || refreshParentFqn === undefined) return;
    if (refreshParentFqn === prevRefreshParentFqnRef.current) return;

    prevRefreshParentFqnRef.current = refreshParentFqn;

    queryClient.invalidateQueries({ queryKey: domainKeys.hierarchy(DOMAIN_HIERARCHY_LIMIT) });

    prevRefreshParentFqnRef.current = null;
    onRefreshComplete?.();
  }, [refreshParentFqn, queryClient, onRefreshComplete]);

  const onCollapseAll = useCallback(() => {
    if (expandedKeys.length === 0) return;
    setExpandedKeys([]);
  }, [expandedKeys.length]);

  const onSelect = useCallback(
    (
      newSelectedKeys: React.Key[],
      info: {
        event: 'select';
        selected: boolean;
        node: EventDataNode<DataNode>;
        selectedNodes: DataNode[];
        nativeEvent: MouseEvent;
      }
    ) => {
      if (!info.selected || newSelectedKeys.length === 0) {
        setSelectedKeys([]);
        return;
      }

      const selectedKey = newSelectedKeys[0] as string;
      const depth = calculateFqnDepth(selectedKey);

      setSelectedKeys(newSelectedKeys);
      navigate(`${ROUTES.DOMAIN}/${encodeURIComponent(selectedKey)}/sub-domains`);

      if (depth >= TREE_DEPTH.DOMAIN) return;
      if (!expandedKeys.includes(selectedKey)) {
        setExpandedKeys([...expandedKeys, selectedKey]);
      }
    },
    [expandedKeys, navigate]
  );

  const onExpand = useCallback((newExpandedKeys: React.Key[]) => {
    const filteredExpandedKeys = newExpandedKeys.filter((key) => {
      const depth = calculateFqnDepth(key as string);
      return depth < TREE_DEPTH.DOMAIN;
    });
    setExpandedKeys(filteredExpandedKeys);
  }, []);

  const prevSelectedDomainFqnRef = useRef<string | null>(selectedDomainFqn);

  useEffect(() => {
    if (selectedDomainFqn !== prevSelectedDomainFqnRef.current) {
      prevSelectedDomainFqnRef.current = selectedDomainFqn;

      if (selectedDomainFqn) {
        setSelectedKeys([selectedDomainFqn]);
      } else if (treeData.length > 0) {
        const firstKey = treeData[0].key as string;
        setSelectedKeys([firstKey]);
        navigate(`${ROUTES.DOMAIN}/${encodeURIComponent(firstKey)}/sub-domains`, { replace: true });
      }
    }
  }, [selectedDomainFqn, treeData, navigate]);

  useEffect(() => {
    if (!breadcrumbPath) return;
    if (treeData.length === 0) return;

    const parts = breadcrumbPath.split('.');
    const keysToExpand: string[] = [];
    for (let i = 1; i <= parts.length; i++) {
      const partialFqn = parts.slice(0, i).join('.');
      keysToExpand.push(partialFqn);
    }
    const filteredKeys = keysToExpand.filter((key) => calculateFqnDepth(key) < TREE_DEPTH.DOMAIN);

    setExpandedKeys((prevKeys) => {
      const newKeysSet = new Set(prevKeys);
      filteredKeys.forEach((key) => newKeysSet.add(key));
      return Array.from(newKeysSet);
    });

    setSelectedKeys([breadcrumbPath]);
    handleSetBreadcrumbPath('');
  }, [breadcrumbPath, treeData, handleSetBreadcrumbPath]);

  useEffect(() => {
    if (treeData.length === 0) return;

    if (!isInitialized && !selectedDomainFqn) {
      const firstDomain = treeData[0];
      if (firstDomain) {
        const domainFqn = firstDomain.key as string;
        setSelectedKeys([domainFqn]);
        setExpandedKeys([domainFqn]);

        setIsInitialized(true);
        navigate(`${ROUTES.DOMAIN}/${encodeURIComponent(domainFqn)}/sub-domains`);
      }
      return;
    }

    if (selectedDomainFqn) {
      setSelectedKeys([selectedDomainFqn]);
      const parts = selectedDomainFqn.split('.');
      const keysToExpand: string[] = [];
      for (let i = 1; i < parts.length; i++) {
        const partialFqn = parts.slice(0, i).join('.');
        keysToExpand.push(partialFqn);
      }
      const filteredKeys = keysToExpand.filter((key) => calculateFqnDepth(key) < TREE_DEPTH.DOMAIN);

      setExpandedKeys((prevKeys) => {
        const newKeysSet = new Set(prevKeys);
        filteredKeys.forEach((key) => newKeysSet.add(key));
        return Array.from(newKeysSet);
      });

      setIsInitialized(true);
      return;
    }
  }, [selectedDomainFqn, treeData, isInitialized, navigate]);

  return (
    <CategoryTree
      headerTitle="도메인 추가"
      isLoading={isLoading}
      showCount={true}
      treeData={treeData}
      expandedKeys={expandedKeys}
      selectedKeys={selectedKeys}
      loadingKeys={[]}
      onCollapseAll={onCollapseAll}
      onSelect={onSelect}
      onExpand={onExpand}
      onAddClick={onAddClick}
      addButtonText="도메인 추가"
      showAddButton={canManageCategories}
    />
  );
};

const Error = (props: React.ComponentProps<typeof ErrorFallback>) => {
  return (
    <div className={styles.error}>
      <ErrorFallback
        {...props}
        title={NOTIFICATION_MESSAGES.DOMAIN_CATEGORY_ERROR}
        description={NOTIFICATION_MESSAGES.DOMAIN_CATEGORY_ERROR_DESC}
      />
    </div>
  );
};

export const DomainCategoryTree = { Tree, Error };
